// src/pages/PaymentSuccess.tsx
// Shown after a successful BOG redirect back to the app.
// On mount: calls refreshSubscriptionInStore() to pull the latest Firestore
// subscription into useAuthStore.profile, then displays the plan name +
// expiry pulled from the freshly-updated profile.

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { refreshSubscriptionInStore, PLAN_NAMES } from '../services/paymentService';
import type { PlanId } from '../stores/usePaymentStore';

const C = {
  text:    '#f1f5f9',
  muted:   '#8b9cc4',
  accent:  '#4f8cf7',
  success: '#34d399',
};

if (typeof document !== 'undefined' && !document.getElementById('esp-spinner-kf')) {
  const s = document.createElement('style');
  s.id = 'esp-spinner-kf';
  s.textContent = `@keyframes esp-spin { to { transform: rotate(360deg); } }`;
  document.head.appendChild(s);
}

function formatDate(d: Date | null | undefined): string {
  if (!d) return '—';
  return d.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' });
}

export default function PaymentSuccess() {
  const navigate       = useNavigate();
  const [searchParams] = useSearchParams();
  const profile        = useAuthStore((s) => s.profile);

  const [loading, setLoading]   = useState(true);
  const [btnHover, setBtnHover] = useState(false);

  const planIdParam = searchParams.get('planId') as PlanId | null;

  useEffect(() => {
    let cancelled = false;
    refreshSubscriptionInStore()
      .catch((e) => console.warn('[PaymentSuccess] refresh failed:', e))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const sub      = profile?.subscription;
  const planId   = sub?.plan ?? planIdParam;
  const planName = planId ? (PLAN_NAMES[planId] ?? planId) : 'Premium Plan';
  const endDate  = sub?.endDate ? formatDate(sub.endDate) : '—';

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', background: '#080c14',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '40px', height: '40px', borderRadius: '50%',
            border: '3px solid rgba(255,255,255,0.06)',
            borderTopColor: C.accent,
            animation: 'esp-spin 0.8s linear infinite',
            margin: '0 auto 20px',
          }} />
          <p style={{ color: C.muted, fontSize: '0.9rem' }}>Confirming your subscription…</p>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh', background: '#080c14',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '24px',
      fontFamily: 'system-ui, -apple-system, "Segoe UI", sans-serif',
    }}>
      <div style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: '24px',
        padding: '52px 44px',
        maxWidth: '480px',
        width: '100%',
        textAlign: 'center',
        backdropFilter: 'blur(20px)',
        boxShadow: '0 32px 64px rgba(0,0,0,0.4)',
      }}>
        {/* Success icon */}
        <div style={{
          width: '72px', height: '72px', borderRadius: '50%',
          background: 'rgba(52,211,153,0.12)',
          border: '1px solid rgba(52,211,153,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 28px',
          boxShadow: '0 0 32px rgba(52,211,153,0.15)',
        }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="20 6 9 17 4 12" />
          </svg>
        </div>

        <h1 style={{
          fontSize: '1.8rem', fontWeight: 900, color: C.text, margin: '0 0 12px',
        }}>
          Payment Successful!
        </h1>

        <p style={{ fontSize: '0.95rem', color: C.muted, margin: '0 0 36px', lineHeight: 1.6 }}>
          Welcome to ESP for Sports Premium. All units are now unlocked — let's get started!
        </p>

        {/* Plan info */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '32px' }}>
          {[
            { label: 'Plan', value: planName },
            { label: 'Access until', value: endDate },
          ].map(({ label, value }) => (
            <div key={label} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '12px 16px',
              background: 'rgba(255,255,255,0.03)',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '12px',
              textAlign: 'left',
            }}>
              <span style={{ fontSize: '0.78rem', color: C.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                {label}
              </span>
              <span style={{ fontSize: '0.9rem', color: C.text, fontWeight: 700 }}>{value}</span>
            </div>
          ))}
        </div>

        <button
          style={{
            width: '100%', padding: '15px',
            borderRadius: '999px',
            background: `linear-gradient(135deg, ${C.success} 0%, #059669 100%)`,
            color: '#fff', fontWeight: 800, fontSize: '1rem',
            border: 'none', cursor: 'pointer', fontFamily: 'inherit',
            transition: 'transform 0.2s ease, box-shadow 0.2s ease',
            transform: btnHover ? 'translateY(-2px)' : 'none',
            boxShadow: btnHover ? '0 12px 28px rgba(52,211,153,0.3)' : 'none',
          }}
          onMouseEnter={() => setBtnHover(true)}
          onMouseLeave={() => setBtnHover(false)}
          onClick={() => navigate('/')}
        >
          Start Learning
        </button>
      </div>
    </div>
  );
}
